import { FingerprintHelper } from '@bcp-channels-sharedcomponents-reactjs/utils';
import { AppHelper, LoggerHelper } from '@helpers';
import { useLoginStore, useSearchParamsStore, useServicesStore } from '@store';
import { ChallengeTypeEnum, CredentialsChallenge, endpoints, SafeCredentialTypeEnum } from '@types';

import { useBrowserInfoCookie, useCookiesConsent } from '../useCookies';

type SubmitFormInput = {
	useCertificateUrl?: boolean;
};

export const useAccountChallengeSubmitForm = () => {
	const language = useSearchParamsStore((state) => state.language);
	const {
		sdcBusinessSessionId,
		sdcSessionId,
		geo,
		targetSystem,
		targetInfrastructure,
		returnUrl,
		userAlias,
		userAliasTypeId,
		challengeType,
		safeCredentialType,
		positionsToAsk,
		needsTaxNumber,
		needsSafe,
	} = useServicesStore((store) => store.accountLogin) || {};
	const captchaId = useServicesStore((store) => store.accountGenerateCaptcha?.id);
	const { form } = useLoginStore();

	const { getFormattedCookiesConsent } = useCookiesConsent();
	const { getBrowserInfoCookie } = useBrowserInfoCookie();

	const getCredentials = () => {
		const { password, multichannel, taxNumber, sms, token } = form || {};

		let credentialsChallenge: CredentialsChallenge[] | undefined = undefined;
		let additionalChallengeType: ChallengeTypeEnum | undefined = undefined;
		let safeCredentialValue: string | undefined = undefined;

		if (
			challengeType === ChallengeTypeEnum.Multichannel ||
			challengeType === ChallengeTypeEnum.MultichannelCompanyOperatorCoHolder
		) {
			credentialsChallenge = positionsToAsk?.map((position, index) => ({
				Position: position,
				Value: multichannel?.[index],
			}));
		}

		if (needsTaxNumber) {
			additionalChallengeType = ChallengeTypeEnum.Nif;
			credentialsChallenge = positionsToAsk?.map((position, index) => ({
				Position: position,
				Value: taxNumber?.[index],
			}));
		}

		if (needsSafe && safeCredentialType === SafeCredentialTypeEnum.Sms) {
			safeCredentialValue = sms;
		}

		if (needsSafe && safeCredentialType === SafeCredentialTypeEnum.Token) {
			safeCredentialValue = (token?.input1 || '') + (token?.input2 || '');
		}

		return {
			password,
			credentialsChallenge: credentialsChallenge ? JSON.stringify(credentialsChallenge) : undefined,
			additionalChallengeType: additionalChallengeType?.toString(),
			safeCredentialValue,
		};
	};

	const submitForm = async (input?: SubmitFormInput) => {
		const { userAgent } = window.navigator;
		const fingerprint = await FingerprintHelper.getFingerprint();
		const cookieConsent = getFormattedCookiesConsent();
		const cookieCifra = getBrowserInfoCookie();

		const { password, credentialsChallenge, additionalChallengeType, safeCredentialValue } = getCredentials();
		const { captchaValue } = form || {};

		const formData = {
			language: language?.toUpperCase(),
			userAgent,
			fingerprint,
			cookieCifra,
			cookieConsent,
			sdcBusinessSessionId,
			sdcSessionId,
			geo,
			targetSystem,
			targetInfrastructure,
			returnUrl,
			userAlias,
			userAliasTypeId: userAliasTypeId?.toString(),
			challengeType: challengeType?.toString(),
			safeCredentialType: safeCredentialType?.toString(),
			...(password && { password }),
			...(credentialsChallenge && { credentialsChallenge }),
			...(additionalChallengeType && { additionalChallengeType }),
			...(safeCredentialValue && { safeCredentialValue }),
			...(captchaId && { captchaId }),
			...(captchaValue && { valueCaptchaClient: captchaValue }),
		};

		const actionUrl = input?.useCertificateUrl ? endpoints.accountChallengeCertificate : endpoints.accountChallenge;

		LoggerHelper.log('App', 'useAccountChallengeSubmitForm > submitForm:', { actionUrl, formData });

		AppHelper.submitForm(actionUrl, formData);
	};

	return { submitForm };
};
